import React, { useEffect, useId, useRef, useState } from 'react';
import { Loader2Icon, MapPinIcon, CheckCircle2Icon, AlertTriangleIcon, InfoIcon } from 'lucide-react';
import type { AddressValue, Lang } from '../../types/funnel';
import { fetchAddress, fetchSuggestions, newSessionToken, placesEnabled, type PlaceSuggestion } from '../../utils/places';
import { validateAddress } from '../../utils/bookingApi';
import { digitsOnly } from '../../utils/format';
import { TextField } from './TextField';
const copy = {
  en: {
    street: 'Street address',
    unit: 'Apt / unit (optional)',
    buildingNo: 'Building number (optional)',
    building: 'Complex or community name (optional)',
    city: 'City',
    state: 'State',
    zip: 'ZIP',
    searching: 'Searching addresses…',
    none: 'No matching addresses. Keep typing, or enter it manually and our team will review it.',
    error: 'Address suggestions are unavailable right now. Type your address and our team will review it.',
    typed: 'Not verified yet. Our team reviews this address.',
    typedManual: 'Address suggestions aren’t connected yet. Type the full address and our team will review it.',
    selected: 'Selected from Google suggestions. Not validated yet.',
    validated: 'Address checked.',
    review: 'We couldn’t fully confirm this address. Our team will review it with you.',
    zipConflict: (g: string, m: string) => `Google lists ZIP ${g} for this address. You entered ${m}.`,
    useZip: (z: string) => `Use ${z}`,
    keepZip: (z: string) => `Keep ${z}`,
    suggestion: 'Suggested correction:',
    useSuggestion: 'Use suggestion',
    keepMine: 'Keep mine'
  },
  es: {
    street: 'Dirección',
    unit: 'Apto / unidad (opcional)',
    buildingNo: 'Número de edificio (opcional)',
    building: 'Nombre del complejo o comunidad (opcional)',
    city: 'Ciudad',
    state: 'Estado',
    zip: 'Código postal',
    searching: 'Buscando direcciones…',
    none: 'No hay direcciones que coincidan. Siga escribiendo o ingrésela manualmente; nuestro equipo la revisará.',
    error: 'Las sugerencias de dirección no están disponibles ahora. Escriba su dirección; nuestro equipo la revisará.',
    typed: 'Sin verificar: nuestro equipo revisa esta dirección.',
    typedManual: 'Las sugerencias de dirección aún no están conectadas. Escriba la dirección completa; nuestro equipo la revisa.',
    selected: 'Seleccionada de las sugerencias de Google; aún no validada.',
    validated: 'Dirección revisada.',
    review: 'No pudimos confirmar completamente esta dirección. Nuestro equipo la revisará con usted.',
    zipConflict: (g: string, m: string) => `Google indica el código postal ${g} para esta dirección. Usted escribió ${m}.`,
    useZip: (z: string) => `Usar ${z}`,
    keepZip: (z: string) => `Mantener ${z}`,
    suggestion: 'Corrección sugerida:',
    useSuggestion: 'Usar sugerencia',
    keepMine: 'Mantener la mía'
  }
};
interface AddressFieldsProps {
  value: AddressValue;
  onChange: (patch: Partial<AddressValue>) => void;
  lang: Lang;
  zipError?: string;
}
type Pending = {
  kind: 'zip';
  google: string;
} | {
  kind: 'fix';
  street: string;
  city: string;
  state: string;
  zip: string;
} | null;
export function AddressFields({
  value,
  onChange,
  lang,
  zipError
}: AddressFieldsProps) {
  const c = copy[lang];
  const uid = useId();
  const enabled = placesEnabled();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<PlaceSuggestion[]>([]);
  const [state, setState] = useState<'idle' | 'loading' | 'error'>('idle');
  const [active, setActive] = useState(-1);
  const [pending, setPending] = useState<Pending>(null);
  const reqId = useRef(0);
  const token = useRef<unknown>(null);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const onStreet = (v: string) => {
    onChange({
      street: v,
      placeId: null,
      status: 'typed'
    });
    setPending(null);
    if (!enabled) return;
    window.clearTimeout(timer.current);
    if (v.trim().length < 3) {
      setItems([]);
      setOpen(false);
      return;
    }
    const id = ++reqId.current;
    setState('loading');
    setOpen(true);
    timer.current = window.setTimeout(async () => {
      try {
        if (!token.current) token.current = await newSessionToken();
        const res = await fetchSuggestions(v, token.current);
        if (id !== reqId.current) return; // discard stale responses
        setItems(res);
        setActive(-1);
        setState('idle');
      } catch {
        if (id !== reqId.current) return;
        setItems([]);
        setState('error');
      }
    }, 200);
  };
  const select = async (s: PlaceSuggestion) => {
    setOpen(false);
    reqId.current++;
    setState('loading');
    try {
      const a = await fetchAddress(s);
      token.current = null; // session ends at selection
      const typedZip = value.zip;
      onChange({
        street: a.street || s.main,
        city: a.city,
        state: a.state,
        placeId: a.placeId,
        status: 'selected',
        ...(typedZip ? {} : {
          zip: a.zip
        })
      });
      if (typedZip && a.zip && typedZip !== a.zip) setPending({
        kind: 'zip',
        google: a.zip
      });
      setState('idle');
      const v = await validateAddress({
        street: a.street,
        unit: value.unit,
        city: a.city,
        state: a.state,
        zip: typedZip || a.zip,
        placeId: a.placeId
      }).catch(() => null);
      if (v) {
        onChange({
          status: v.verdict
        });
        if (v.suggested) setPending({
          kind: 'fix',
          ...v.suggested
        });
      }
    } catch {
      setState('error');
    }
  };
  const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open || items.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive(i => Math.min(items.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive(i => Math.max(0, i - 1));
    } else if (e.key === 'Enter' && active >= 0) {
      e.preventDefault();
      void select(items[active]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };
  const status = value.status === 'validated' ? {
    icon: <CheckCircle2Icon className="h-4 w-4 text-lime-dark" data-id="element-387" data-mp="28dtlcyzp84yk" />,
    text: c.validated
  } : value.status === 'needs_review' ? {
    icon: <AlertTriangleIcon className="h-4 w-4 text-magenta-dark" data-id="element-388" data-mp="2m5q8spqf9qom" />,
    text: c.review
  } : value.status === 'selected' ? {
    icon: <MapPinIcon className="h-4 w-4 text-ink-500" data-id="element-389" data-mp="2w2l5d334xh09" />,
    text: c.selected
  } : {
    icon: <InfoIcon className="h-4 w-4 text-ink-500" data-id="element-390" data-mp="2w4fu6shj1t9m" />,
    text: enabled ? c.typed : c.typedManual
  };
  const listId = `${uid}-list`;
  return <div className="space-y-3" data-id="element-391" data-mp="353jc3t4yk2mj">
      <div className="relative" data-id="element-392" data-mp="352rholn3kaxu">
        <label htmlFor={`${uid}-street`} className="mb-1.5 block text-sm font-semibold text-ink" data-id="element-393" data-mp="3046q1alwuzl0">
          {c.street}
        </label>
        <div className="relative" data-id="element-394" data-mp="1aogtmfebtkt4">
          <input id={`${uid}-street`} role={enabled ? 'combobox' : undefined} aria-expanded={enabled ? open : undefined} aria-controls={enabled ? listId : undefined} aria-autocomplete={enabled ? 'list' : undefined} aria-activedescendant={active >= 0 ? `${listId}-${active}` : undefined} autoComplete="off" value={value.street} onChange={e => onStreet(e.target.value)} onKeyDown={onKey} onBlur={() => window.setTimeout(() => setOpen(false), 150)} onFocus={() => items.length > 0 && setOpen(true)} className="h-12 w-full rounded-lg border-2 border-ink-200 bg-white px-4 pr-10 text-base text-ink focus:border-ink focus:outline-none" data-id="element-395" data-mp="1apidn9dblhmz" />
          {state === 'loading' && <Loader2Icon className="absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 animate-spin text-ink-400" aria-label={c.searching} data-id="element-396" data-mp="2tny6qde030vf" />}
        </div>
        {enabled && open && <ul id={listId} role="listbox" className="absolute z-20 mt-1 max-h-72 w-full overflow-y-auto rounded-lg bg-white py-1 shadow-lg ring-1 ring-ink-200" data-id="element-397" data-mp="nsvfz6mqspi1">
            {state === 'loading' && items.length === 0 && <li className="px-4 py-3 text-sm text-ink-500" data-id="element-398" data-mp="3bughkv5gllh1">{c.searching}</li>}
            {state === 'error' && <li className="px-4 py-3 text-sm text-magenta-dark" data-id="element-399" data-mp="36vvgktiduxrj">{c.error}</li>}
            {state === 'idle' && items.length === 0 && <li className="px-4 py-3 text-sm text-ink-500" data-id="element-400" data-mp="2x3zdf41fwhgg">{c.none}</li>}
            {items.map((s, i) => <li key={s.id} id={`${listId}-${i}`} role="option" aria-selected={i === active} onMouseDown={e => {
          e.preventDefault();
          void select(s);
        }} className={`flex cursor-pointer items-start gap-2 px-4 py-2.5 text-sm ${i === active ? 'bg-ink-100' : 'hover:bg-ink-100'}`} data-id="element-401" data-mp="2t2nq5561g8bf" data-mpi={i}>
                <MapPinIcon className="mt-0.5 h-4 w-4 shrink-0 text-ink-400" aria-hidden="true" data-id="element-402" data-mp="hbsdqz5ck747" />
                <span data-id="element-403" data-mp="cea1ozw19brz">
                  <span className="font-semibold text-ink" data-id="element-404" data-mp="cbnxna8cpeei">{s.main}</span>
                  {s.secondary && <span className="block text-xs text-ink-500" data-id="element-405" data-mp="kseoa7irlkmk">{s.secondary}</span>}
                </span>
              </li>)}
          </ul>}
      </div>

      <div className="grid grid-cols-2 gap-3" data-id="element-406" data-mp="tnco7ykbxxne">
        <TextField label={c.unit} value={value.unit} onChange={v => onChange({
        unit: v.slice(0, 20)
      })} data-id="element-407" data-mp="ylmjsa9yv09j" />
        <TextField label={c.buildingNo} value={value.buildingNo ?? ''} onChange={v => onChange({
        buildingNo: v.slice(0, 20)
      })} data-id="element-408" data-mp="1w54r4p3g1v6" />
      </div>
      <TextField label={c.building} value={value.building} onChange={v => onChange({
      building: v.slice(0, 80)
    })} data-id="element-409" data-mp="5xqnv7s81cdt" />
      <div className="grid grid-cols-[1fr_72px_110px] gap-3" data-id="element-410" data-mp="ayee3r8mlp6z">
        <TextField label={c.city} value={value.city} onChange={v => onChange({
        city: v,
        placeId: null,
        status: 'typed'
      })} data-id="element-411" data-mp="fwfu23ar98e8" />
        <TextField label={c.state} value={value.state} maxLength={2} onChange={v => onChange({
        state: v.toUpperCase().slice(0, 2),
        placeId: null,
        status: 'typed'
      })} data-id="element-412" data-mp="1mb8ikwmffdh0" />
        <TextField label={c.zip} inputMode="numeric" maxLength={5} value={value.zip} error={zipError} onChange={v => onChange({
        zip: digitsOnly(v).slice(0, 5),
        status: value.status === 'selected' ? 'selected' : 'typed'
      })} data-id="element-413" data-mp="2x2u4h60qzgui" />
      </div>

      {pending?.kind === 'zip' && <div role="status" className="rounded-lg bg-ink-100 p-3 text-sm text-ink" data-id="element-414" data-mp="3avtewtloakv7">
          {c.zipConflict(pending.google, value.zip)}
          <div className="mt-2 flex gap-2" data-id="element-415" data-mp="2e6lsljd8c7q8">
            <button type="button" onClick={() => {
          onChange({
            zip: pending.google
          });
          setPending(null);
        }} className="min-h-[40px] rounded-md bg-ink px-3 font-semibold text-white" data-id="element-416" data-mp="2j441xav5soyr">
              {c.useZip(pending.google)}
            </button>
            <button type="button" onClick={() => {
          onChange({
            status: 'needs_review'
          });
          setPending(null);
        }} className="min-h-[40px] rounded-md border-2 border-ink-200 bg-white px-3 font-semibold text-ink" data-id="element-417" data-mp="2s5lisvge4uoi">
              {c.keepZip(value.zip)}
            </button>
          </div>
        </div>}
      {pending?.kind === 'fix' && <div role="status" className="rounded-lg bg-ink-100 p-3 text-sm text-ink" data-id="element-418" data-mp="hkcjtbt62pzm">
          {c.suggestion} <span className="font-semibold" data-id="element-419" data-mp="clrif926i09l">{[pending.street, pending.city, pending.state, pending.zip].filter(Boolean).join(', ')}</span>
          <div className="mt-2 flex gap-2" data-id="element-420" data-mp="3rmtiv6luywp">
            <button type="button" onClick={() => {
          onChange({
            street: pending.street,
            city: pending.city,
            state: pending.state,
            zip: pending.zip
          });
          setPending(null);
        }} className="min-h-[40px] rounded-md bg-ink px-3 font-semibold text-white" data-id="element-421" data-mp="3qv0ns4e7fa4">
              {c.useSuggestion}
            </button>
            <button type="button" onClick={() => {
          onChange({
            status: 'needs_review'
          });
          setPending(null);
        }} className="min-h-[40px] rounded-md border-2 border-ink-200 bg-white px-3 font-semibold text-ink" data-id="element-422" data-mp="3kzic1cqnlyq5">
              {c.keepMine}
            </button>
          </div>
        </div>}

      <p className="flex items-start gap-2 text-xs text-ink-600" data-id="element-423" data-mp="1f6rzq95y3fl2">
        <span className="mt-px shrink-0" aria-hidden="true" data-id="element-424" data-mp="38oqy6lgx1hbd">
          {status.icon}
        </span>
        {status.text}
      </p>
    </div>;
}